"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
class BaseVector {
    constructor(x, y) {
        this.x = round(x);
        this.y = round(y);
    }
    static fromRadians(radians) {
        return new this(Math.cos(radians), Math.sin(radians));
    }
    static fromDegrees(degrees) {
        return this.fromRadians(degreesToRadians(degrees));
    }
    static fromMagnitude(value) {
        return new this(value, 0);
    }
    static from(degrees, magnitude) {
        const vector = this.fromDegrees(degrees);
        return new this(vector.x * magnitude, vector.y * magnitude);
    }
    static merge(vectorA, vectorB, ...others) {
        let x = vectorA.x + vectorB.x;
        let y = vectorA.y + vectorB.y;
        if (others.length) {
            for (const vector of others) {
                x += vector.x;
                y += vector.y;
            }
        }
        return new this(x, y);
    }
    static diff(vectorA, vectorB, ...others) {
        let x = vectorA.x - vectorB.x;
        let y = vectorA.y - vectorB.y;
        if (others.length) {
            for (const vector of others) {
                x -= vector.x;
                y -= vector.y;
            }
        }
        return new this(x, y);
    }
    get isZero() {
        return this.x === 0 && this.y === 0;
    }
    get radians() {
        if (this.isZero)
            return 0;
        let arctan = Math.atan(this.y / this.x);
        if (arctan < 0)
            arctan += Math.PI;
        if (this.y < 0 || (this.y === 0 && this.x < 0))
            arctan += Math.PI;
        return arctan;
    }
    get degrees() {
        const degrees = (this.radians / Math.PI * 180) % 360;
        return degrees < 0 ? degrees + 360 : degrees;
    }
    get magnitude() {
        return this.isZero ? 0 : round(Math.sqrt(this.x * this.x + this.y * this.y));
    }
    toJSON() {
        return `{x:${this.x},y:${this.y}}`;
    }
    toString() {
        return `[Vector(${this.x},${this.y})]`;
    }
    add(x, y = x) {
        return this.set(this.x + x, this.y + y);
    }
    sustract(x, y = x) {
        return this.set(this.x - x, this.y - y);
    }
    multiply(x, y = x) {
        return this.set(this.x * x, this.y * y);
    }
    divide(x, y = x) {
        return this.set(this.x / x, this.y / y);
    }
    merge(other) {
        return this.set(this.x + other.x, this.y + other.y);
    }
    diff(other) {
        return this.set(this.x - other.x, this.y - other.y);
    }
    round() {
        return this.set(round(this.x), round(this.y));
    }
    abs() {
        return this.apply(Math.abs);
    }
    apply(operation) {
        return this.set(operation(this.x), operation(this.y));
    }
}
BaseVector.round = round;
/*
 * This version of Vector is immutable, any method that requires a modification
 * of the properties will return a new Vector.
 * If you want mutability you can import { MutableVector } instead
 */
class Vector extends BaseVector {
    set(x, y) {
        return new Vector(x, y);
    }
    toMutable() {
        return new MutableVector(this.x, this.y);
    }
}
Vector.ZERO = new Vector(0, 0);
Vector.MAX = new Vector(Infinity, Infinity);
exports.default = Vector;
class MutableVector extends BaseVector {
    set radians(value) {
        const magnitude = this.magnitude;
        this.x = Math.cos(value) * magnitude;
        this.y = Math.sin(value) * magnitude;
    }
    set degrees(value) {
        this.radians = degreesToRadians(value);
    }
    set magnitude(value) {
        const prevMagnitude = this.magnitude;
        this.x = Math.cos(value) * prevMagnitude;
        this.y = Math.sin(value) * prevMagnitude;
    }
    set(x, y) {
        this.x = x;
        this.y = y;
        return this;
    }
    clone() {
        return new MutableVector(this.x, this.y);
    }
    toImmutable() {
        return new Vector(this.x, this.y);
    }
}
exports.MutableVector = MutableVector;
function degreesToRadians(degrees) {
    degrees = degrees % 360;
    if (degrees < 0)
        degrees += 360;
    return degrees * Math.PI / 180;
}
function round(value) {
    return Math.round(value * 100) / 100;
}



//////////////////
// WEBPACK FOOTER
// ./src/core/vector.ts
// module id = 0
// module chunks = 0